import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import type React from "react";
import { cva } from "class-variance-authority";
import clsx from "clsx";
import { Button, EditableText, Flex, Input, Text, message } from "@nula/core-ui";

import { apiFetch } from "@/lib/http/client";
import { useEntityStore } from "@/lib/sync/entity-store";

import {
  useEntityAttendees,
  invalidateEntityAttendees,
  type AttendeeSuggestion,
} from "@/lib/recordings/use-entity-attendees";
import { resolveSpeakerName } from "@/lib/recordings/resolve-speaker-name";
import { buildAttendeeNameMap } from "@/lib/recordings/attendee-name-map";
import { useRecording } from "../hooks/use-recording";
import { useSpeakerLabelInFlight } from "../hooks/use-speaker-label-in-flight";
import { SpeakerHoverCard } from "./speaker-hover-card";
import type { SpeakerLabel } from "@/lib/recordings/types/types";
import styles from "./speaker-label-edit.module.css";

// ── CVA ───────────────────────────────────────────────────────────────────────

const root = cva(styles.root);

// ── Types ─────────────────────────────────────────────────────────────────────

export type SpeakerLabelEditProps = {
  recordingId: string;
  speakerId: number;
  /**
   * The entity the attendees live on (the recording's notes entity). The
   * attendees endpoint is keyed by (entity, surface): a surface's roster is
   * its own linked event's invitees union its own manual rows, with the
   * document-level rows (surfaceId omitted) as the pre-M7 fallback. A
   * recording can map to several entities, so the consumer (transcript
   * reader) passes the one it subscribes to. Falls back to the recording's
   * stored entityId when absent.
   */
  entityId?: string | null;
  /** This recording's audio surface — scopes the attendee roster to this
   * surface. Omitted reproduces the document-level roster. */
  surfaceId?: string | null;
  /** Optional class merged onto the root span. */
  className?: string;
  /**
   * Retained for backward compatibility — no longer read internally. The
   * attendee dropdown is now sourced from the merged attendees endpoint
   * (`/api/entities/:entityId/attendees`) which handles calendar attendees,
   * manually-added attendees, AND Apollo-enriched display names.
   * Drop from callers when refactoring TranscriptSegmentRow.
   */
  calendarEventId?: string | null;
};

// ── Internal helpers ──────────────────────────────────────────────────────────

function nameFromEmail(email: string | null | undefined): string | null {
  if (!email) return null;
  const at = email.indexOf("@");
  if (at <= 0) return null;
  const local = email.slice(0, at).trim();
  return local || null;
}

/**
 * Optimistically write a label entry into the entity store. Returns the
 * previous entry (or undefined when none) so the caller can revert on error.
 */
function applyOptimisticLabel(
  recordingId: string,
  speakerKey: string,
  next: SpeakerLabel | undefined,
): SpeakerLabel | undefined {
  let previous: SpeakerLabel | undefined;
  useEntityStore.setState((s) => {
    const rec = s.recordings.get(recordingId);
    if (!rec) return s;
    previous = rec.speakerLabels[speakerKey];
    const labels = { ...rec.speakerLabels };
    if (next === undefined) {
      delete labels[speakerKey];
    } else {
      labels[speakerKey] = next;
    }
    const updated = { ...rec, speakerLabels: labels };
    const recordings = new Map(s.recordings);
    recordings.set(recordingId, updated);
    return { ...s, recordings };
  });
  return previous;
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * Inline-editable speaker label. The display label is the email's local-part.
 * Saving an empty email deletes the entry server-side.
 */
export function SpeakerLabelEdit({
  recordingId,
  speakerId,
  entityId: entityIdProp,
  surfaceId,
  className,
  // calendarEventId is retained on the prop type for backward compatibility
  // (TranscriptSegmentRow still passes it) but no longer read — the merged
  // attendees endpoint replaces it. Prefix with `_` so tsc doesn't flag the
  // unused destructure; remove the prop entirely when refactoring callers.
  calendarEventId: _calendarEventId,
}: SpeakerLabelEditProps) {
  const recording = useRecording(recordingId);
  const speakerKey = String(speakerId);
  const current: SpeakerLabel | undefined = recording?.speakerLabels[speakerKey];
  // Attendees are keyed by entity; prefer the consumer-supplied entity (the
  // entity the transcript reader subscribes to) over the recording's own,
  // which can point at a different linked entity (canvas vs document).
  const attendeeEntityId = entityIdProp ?? recording?.entityId ?? null;

  const [editing, setEditing] = useState(false);
  const [emailValue, setEmailValue] = useState(current?.email ?? "");
  // Active suggestion index for keyboard nav (Arrow Up/Down, Home/End,
  // Enter to apply). -1 = nothing active.
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  // Typed as the generic Flex forwarded element (HTMLElement) instead of
  // HTMLDivElement — Flex defaults to <div> today but its forwardRef
  // signature is HTMLElement. Tightening the local ref to the actual
  // forwarded type keeps us correct if Flex ever changes its default tag.
  const containerRef = useRef<HTMLElement | null>(null);
  // Wraps the EditableText in display mode — we use it to find the role=button
  // surface after editing closes, so keyboard focus returns to the trigger
  // instead of dropping to document.body.
  const triggerWrapperRef = useRef<HTMLSpanElement | null>(null);
  // Tracked synchronously so the in-flight PATCH's catch path can bail
  // without touching the entity store after the component unmounts. The
  // AbortController short-circuits the fetch itself.
  const mountedRef = useRef(true);
  const abortRef = useRef<AbortController | null>(null);

  // Reset draft value whenever the underlying entry changes (e.g., a peer
  // edit lands while the field is closed).
  useEffect(() => {
    if (!editing) {
      setEmailValue(current?.email ?? "");
    }
  }, [current?.email, editing]);

  // Shared in-flight gate so two SpeakerLabelEdit instances bound to the
  // same (recordingId, speakerId) can't race each other into a
  // failure-revert clobber. See useSpeakerLabelInFlight for details.
  const tryAcquireInFlight = useSpeakerLabelInFlight((s) => s.tryAcquire);
  const releaseInFlight = useSpeakerLabelInFlight((s) => s.release);

  // Track mount state + abort any in-flight save on unmount so the catch
  // path doesn't clobber a peer edit that arrived after the panel closed.
  // Also release the shared in-flight gate so a sibling instance can save
  // immediately after we unmount.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
      releaseInFlight(recordingId, speakerId);
    };
  }, [recordingId, speakerId, releaseInFlight]);

  // Suggestion source now comes from the merged attendees endpoint
  // (calendar + manual + Apollo-enriched) keyed by the recording's entity.
  // `calendarEventId` is no longer a gate — impromptu recordings without a
  // calendar event still surface manually-added attendees and previously
  // enriched contacts from this workspace.
  // Fetch always (not just while editing) so the resting display can resolve
  // the labeled email to the attendee's name. Module-cached per entity → cheap.
  const { suggestions: attendees, loading: attendeesLoading } =
    useEntityAttendees(attendeeEntityId, surfaceId);

  // Display name resolution — delegate to the shared resolveSpeakerName so the
  // inline label, the avatar and the chips all resolve identically (attendee
  // name by email, reverting to "Speaker {id}" when the attendee is removed —
  // NOT the stale stored label.name).
  const displayLabel = useMemo(() => {
    // Includes non-enriched attendees (by email local-part) so a speaker
    // assigned to a "Couldn't enrich" attendee still shows their name.
    const attendeeNameByEmail = buildAttendeeNameMap(attendees);
    return resolveSpeakerName(
      { speaker: undefined, speaker_id: speakerId },
      current ? { [speakerKey]: current } : {},
      attendeeNameByEmail,
    );
  }, [attendees, current, speakerId, speakerKey]);

  // While attendees load and the label has an email that hasn't resolved yet,
  // show a skeleton instead of flashing "Speaker {id}" before the real name.
  const resolving =
    attendeesLoading &&
    !!current?.email &&
    !attendees.some((a) => a.email.toLowerCase() === current.email!.toLowerCase() && !!a.name);

  // Stable IDs for aria-activedescendant wiring on the combobox-style input.
  const listboxId = useId();
  const optionId = useCallback(
    (index: number) => `${listboxId}-opt-${index}`,
    [listboxId],
  );
  const activeOptionId = useMemo(
    () => (activeIndex >= 0 ? optionId(activeIndex) : undefined),
    [activeIndex, optionId],
  );

  // Reset active index when the editor opens/closes. Don't reset on
  // attendees.length change — that fires asynchronously when the fetch
  // resolves and races with the user's first ArrowDown keypress on slow
  // runners, clobbering activeIndex back to -1 after the keydown bumped it
  // to 0. The keyboard navigators (ArrowDown/Up) already use modulo so an
  // out-of-range index from a shrunk list is harmless.
  useEffect(() => {
    setActiveIndex(-1);
  }, [editing]);

  const acceptSuggestion = useCallback((s: AttendeeSuggestion) => {
    setEmailValue(s.email);
  }, []);

  // Arrow Up/Down/Home/End navigation across the suggestions listbox.
  const handleListNav = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (attendees.length === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % attendees.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => (i <= 0 ? attendees.length - 1 : i - 1));
      } else if (e.key === "Home") {
        e.preventDefault();
        setActiveIndex(0);
      } else if (e.key === "End") {
        e.preventDefault();
        setActiveIndex(attendees.length - 1);
      }
    },
    [attendees.length],
  );

  const enterEdit = useCallback(() => {
    setEmailValue(current?.email ?? "");
    setEditing(true);
  }, [current?.email]);

  const exitEdit = useCallback(() => {
    setEditing(false);
    // Return focus to the trigger so keyboard users don't lose their place.
    // Defer to the next animation frame so the EditableText display surface
    // has rendered before we try to focus it.
    requestAnimationFrame(() => {
      const trigger = triggerWrapperRef.current?.querySelector<HTMLElement>(
        '[role="button"]',
      );
      trigger?.focus();
    });
  }, []);

  const save = useCallback(async (overrideEmail?: string) => {
    // Shared gate keyed by (recordingId, speakerId) — see
    // useSpeakerLabelInFlight. Returns false when another instance bound
    // to the same speaker is already mid-PATCH.
    if (!tryAcquireInFlight(recordingId, speakerId)) return;
    const trimmedEmail = (overrideEmail ?? emailValue).trim();
    const derivedName = nameFromEmail(trimmedEmail);

    // Optimistic write — derive the next entry from the trimmed email.
    const nextEntry: SpeakerLabel | undefined =
      trimmedEmail === ""
        ? undefined
        : {
            ...(derivedName ? { name: derivedName } : {}),
            email: trimmedEmail,
          };
    const previous = applyOptimisticLabel(recordingId, speakerKey, nextEntry);

    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await apiFetch(`/recordings/${recordingId}/speaker-labels`, {
        method: "PATCH",
        body: JSON.stringify({
          speakerId,
          // Send `null` (not `""`) when fields are cleared so the wire
          // shape matches the server's `Option<String>` contract — empty
          // strings round-trip as Some("") which would diverge from
          // PatchSpeakerLabelRequest's #[serde(default)] = None semantics.
          name: derivedName || null,
          email: trimmedEmail || null,
        }),
        signal: controller.signal,
      });
      // If the typed email is a brand-new person (not already an attendee),
      // add them to the entity's attendees so the name/photo resolve and they
      // appear in the panel. Existing attendees are just relabeled — no POST.
      const entityId = attendeeEntityId;
      if (entityId && trimmedEmail) {
        const isExisting = attendees.some(
          (a) => a.email.toLowerCase() === trimmedEmail.toLowerCase(),
        );
        if (!isExisting) {
          try {
            const url = surfaceId
              ? `/entities/${entityId}/attendees?surfaceId=${encodeURIComponent(surfaceId)}`
              : `/entities/${entityId}/attendees`;
            await apiFetch(url, {
              method: "POST",
              body: JSON.stringify({ email: trimmedEmail }),
            });
            invalidateEntityAttendees(entityId, surfaceId);
          } catch {
            // Non-fatal: the speaker label saved; the attendee add is a bonus.
            // (e.g. 409 already_attendee from a race — safe to ignore.)
          }
        }
      }
      if (!mountedRef.current) return;
      exitEdit();
    } catch (err: unknown) {
      // Aborted requests are the unmount-cleanup path — already handled.
      if (controller.signal.aborted) return;
      if (!mountedRef.current) return;
      // Revert: restore the prior entry. previous === undefined means there
      // was no prior entry, in which case we delete the optimistic one.
      applyOptimisticLabel(recordingId, speakerKey, previous);
      const detail = err instanceof Error ? err.message : "save failed";
      message.error(`Failed to save speaker label: ${detail}`);
      exitEdit();
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
      }
      releaseInFlight(recordingId, speakerId);
    }
  }, [
    recordingId,
    speakerKey,
    speakerId,
    emailValue,
    exitEdit,
    tryAcquireInFlight,
    releaseInFlight,
    attendees,
    attendeeEntityId,
    surfaceId,
  ]);

  // Close on click outside.
  useEffect(() => {
    if (!editing) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current && !containerRef.current.contains(target)) {
        // Treat outside click as an implicit save (matches the blur-to-save
        // pattern the brief calls for).
        void save();
      }
    };
    window.addEventListener("mousedown", handler);
    return () => window.removeEventListener("mousedown", handler);
  }, [editing, save]);

  if (!editing && resolving) {
    return (
      <span ref={triggerWrapperRef} className={clsx(root(), className)}>
        <span
          data-testid="speaker-label-loading"
          aria-hidden
          className={styles.loadingBar}
        />
      </span>
    );
  }

  if (!editing) {
    return (
      <span ref={triggerWrapperRef} className={clsx(root(), className)}>
        <SpeakerHoverCard
          entityId={attendeeEntityId}
          email={current?.email ?? null}
        >
          <EditableText
            value={displayLabel}
            // Never fires — we swap to the compound editor before EditableText
            // enters its own input mode. Required for type satisfaction.
            onChange={() => {}}
            editing={false}
            onEditingChange={(next) => {
              if (next) enterEdit();
            }}
            renderDisplay={(v) => (
              <span
                className={styles.readonly}
                aria-label={`Edit speaker label for ${v}`}
              >
                {v}
              </span>
            )}
          />
        </SpeakerHoverCard>
      </span>
    );
  }

  return (
    <span className={clsx(root(), className)}>
      <Flex
        vertical
        gap="xs"
        className={styles.editContainer}
        ref={containerRef}
      >
        <Flex align="center" gap="xs" className={styles.inputRow}>
          <Input
            size="sm"
            type="email"
            label="Email"
            value={emailValue}
            autoFocus
            className={styles.emailField}
            role="combobox"
            aria-expanded={attendees.length > 0}
            aria-controls={attendees.length > 0 ? listboxId : undefined}
            aria-activedescendant={activeOptionId}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setEmailValue(e.target.value)
            }
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (activeIndex >= 0 && attendees[activeIndex]) {
                  const suggestion = attendees[activeIndex];
                  acceptSuggestion(suggestion);
                  void save(suggestion.email);
                } else {
                  void save();
                }
              } else if (e.key === "Escape") {
                e.preventDefault();
                exitEdit();
              } else {
                handleListNav(e);
              }
            }}
          />
        </Flex>

        {attendees.length > 0 && (
          <Flex
            vertical
            role="listbox"
            id={listboxId}
            className={styles.suggestions}
          >
            {attendees.map((a: AttendeeSuggestion, index: number) => {
              const isActive = index === activeIndex;
              return (
                <Button
                  key={a.email}
                  variant="text"
                  size="sm"
                  block
                  role="option"
                  id={optionId(index)}
                  aria-selected={isActive}
                  className={clsx(
                    styles.suggestionItem,
                    isActive && styles.suggestionItemActive,
                  )}
                  // Prevent the default mousedown action so the input keeps
                  // focus while the picker fires its onClick (otherwise the
                  // implicit blur would close the picker before the click
                  // could register).
                  onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) =>
                    e.preventDefault()
                  }
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => {
                    acceptSuggestion(a);
                    void save(a.email);
                  }}
                >
                  <Flex vertical align="start" className={styles.suggestionStack}>
                    <Text className={styles.suggestionName}>
                      {a.name ?? a.email}
                    </Text>
                    {a.name && (
                      <Text type="secondary" className={styles.suggestionEmail}>
                        {a.email}
                      </Text>
                    )}
                  </Flex>
                </Button>
              );
            })}
          </Flex>
        )}
      </Flex>
    </span>
  );
}
